import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import useCenterlineAiStyles from './styles';
import {
  emptyFilesText,
  EXTENSION_COLORS,
  extensionBadge,
  extensionFamily,
  extensionTitle,
  FILE_LIMIT,
  filesCacheKey,
  filesHeadingNote,
  isFileSelected,
  isTypeSelected,
  NO_DOC_TYPES_TEXT,
  pickerFooter,
  SEARCH_DEBOUNCE_MS,
  selectionCount,
  TOO_MANY_FILES_TEXT,
} from './core/context-selection';
import { useCenterlineAi } from './CenterlineAiProvider';
import { CheckGlyph, CloseGlyph, SearchGlyph } from './Glyphs';

/**
 * The "@" context picker (chat-ui-spec §4). Whole document types, single files, or both narrow
 * what the answer may search. It stays open for picking several; arrow keys move between rows,
 * Enter or Space ticks one, and Escape closes it.
 */

/** Clicks on elements carrying this attribute do not close the picker (the chips, the textarea). */
export const KEEP_PICKER_ATTR = 'data-cb-keep-picker';

function Tick({ on }: { on: boolean }) {
  const classes = useCenterlineAiStyles();
  return (
    <span className={clsx(classes.tick, on && classes.tickOn)} aria-hidden>
      {on ? <CheckGlyph /> : null}
    </span>
  );
}

/** The "@" chips in the composer field (§4.7). */
export function ContextChips() {
  const classes = useCenterlineAiStyles();
  const { state, actions, inputRef } = useCenterlineAi();
  const { selection } = state.context;
  if (selectionCount(selection) === 0) return null;
  const remove = (fn: () => void) => () => {
    fn();
    inputRef.current?.focus();
  };
  return (
    <span className={classes.contextChips} {...{ [KEEP_PICKER_ATTR]: '' }}>
      {selection.types.map((t) => (
        <span
          key={`t:${t}`}
          className={clsx(classes.chip, classes.chipType)}
          title={`All files of type "${t}"`}
          {...{ [KEEP_PICKER_ATTR]: '' }}
        >
          <span className={classes.chipLabel}>@{t}</span>
          <button
            type="button"
            className={clsx(classes.chipX, classes.chipXOnNavy)}
            aria-label={`Remove @${t} from the context`}
            onClick={remove(() => actions.removeType(t))}
          >
            <CloseGlyph />
          </button>
        </span>
      ))}
      {selection.files.map(([id, name]) => (
        <span key={`f:${id}`} className={classes.chip} title={name} {...{ [KEEP_PICKER_ATTR]: '' }}>
          <span className={classes.chipLabel}>{name}</span>
          <button
            type="button"
            className={classes.chipX}
            aria-label={`Remove ${name} from the context`}
            onClick={remove(() => actions.removeFile(id))}
          >
            <CloseGlyph />
          </button>
        </span>
      ))}
      {selectionCount(selection) > 1 ? (
        <button
          type="button"
          className={classes.clearChips}
          onClick={remove(actions.clearContext)}
          {...{ [KEEP_PICKER_ATTR]: '' }}
        >
          clear
        </button>
      ) : null}
    </span>
  );
}

export default function ContextPicker() {
  const classes = useCenterlineAiStyles();
  const { state, actions, inputRef } = useCenterlineAi();
  const { context, projectId } = state;
  const { selection, docTypes, docFilter } = context;
  // Each opening starts with an empty search; the type dropdown is kept (§4.3).
  const [term, setTerm] = useState('');
  const [applied, setApplied] = useState('');
  const search = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);

  useEffect(() => {
    search.current?.focus();
  }, []);

  useEffect(() => {
    const trimmed = term.trim();
    if (!trimmed) {
      setApplied('');
      return undefined;
    }
    const timer = window.setTimeout(() => setApplied(trimmed), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [term]);

  useEffect(() => {
    actions.loadFiles(docFilter, applied);
  }, [actions, docFilter, applied, projectId]);

  const close = () => {
    actions.closePicker();
    inputRef.current?.focus();
  };

  const files = context.fileCache[filesCacheKey(docFilter, applied)];
  const hasTypes = !!docTypes && docTypes.length > 0;

  const moveFocus = (from: HTMLElement | null, step: 1 | -1 | 'first') => {
    const rows = Array.from(list.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? []);
    if (!rows.length) return;
    if (step === 'first') {
      rows[0].focus();
      return;
    }
    const at = from ? rows.indexOf(from) : -1;
    const next = at + step;
    if (next < 0) search.current?.focus();
    else rows[Math.min(next, rows.length - 1)].focus();
  };

  const rowKeys = (toggle: () => void) => (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveFocus(e.currentTarget, 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveFocus(e.currentTarget, -1);
    }
  };

  return (
    <ClickAwayListener
      onClickAway={(e) => {
        if ((e.target as HTMLElement | null)?.closest?.(`[${KEEP_PICKER_ATTR}]`)) return;
        actions.closePicker();
      }}
    >
      <div
        className={classes.picker}
        role="dialog"
        aria-label="Add document context"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            close();
          }
        }}
      >
        <div className={classes.pickerHead}>
          <label className={classes.pickerSearch}>
            <SearchGlyph />
            <input
              ref={search}
              type="search"
              placeholder="Search files…"
              aria-label="Search files"
              autoComplete="off"
              spellCheck={false}
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={(e) => {
                // Enter here never sends the question (§11 quirk 8).
                if (e.key === 'Enter') e.preventDefault();
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  moveFocus(null, 'first');
                }
              }}
            />
          </label>
          <select
            className={classes.pickerSelect}
            aria-label="Filter files by document type"
            disabled={!hasTypes}
            value={docFilter}
            onChange={(e) => actions.setDocFilter(e.target.value)}
          >
            <option value="">All document types</option>
            {(docTypes ?? []).map((t) => (
              <option key={t.doc_type} value={t.doc_type}>
                {`${t.doc_type} (${t.file_count.toLocaleString()})`}
              </option>
            ))}
          </select>
        </div>
        <div
          ref={list}
          className={classes.pickerList}
          role="listbox"
          aria-multiselectable="true"
          aria-label="Documents"
        >
          {!hasTypes ? (
            <div className={classes.pickerMessage}>{NO_DOC_TYPES_TEXT}</div>
          ) : (
            <>
              <div className={classes.pickerSection} role="presentation">
                <span>Document types</span>
                <span className={classes.sectionNote}>pick a whole class</span>
              </div>
              {docTypes!.map((t) => {
                const on = isTypeSelected(selection, t.doc_type);
                const toggle = () => actions.toggleType(t.doc_type);
                return (
                  <div
                    key={t.doc_type}
                    role="option"
                    aria-selected={on}
                    tabIndex={0}
                    className={clsx(classes.pickerRow, on && classes.pickerRowOn)}
                    onClick={toggle}
                    onKeyDown={rowKeys(toggle)}
                  >
                    <Tick on={on} />
                    <span
                      className={clsx(classes.rowName, classes.rowNameStrong)}
                      title={t.doc_type}
                    >
                      {t.doc_type}
                    </span>
                    <span className={classes.rowMeta}>{t.file_count.toLocaleString()}</span>
                  </div>
                );
              })}
              <div
                className={clsx(classes.pickerSection, classes.pickerSectionNext)}
                role="presentation"
              >
                <span>Files</span>
                <span className={classes.sectionNote}>{filesHeadingNote(docFilter)}</span>
              </div>
              {files === undefined ? (
                <div className={classes.pickerMessage}>Loading files…</div>
              ) : files.length === 0 ? (
                <div className={classes.pickerMessage}>{emptyFilesText(applied)}</div>
              ) : (
                files.map((f) => {
                  const on = isFileSelected(selection, f);
                  const toggle = () => actions.toggleFile(f);
                  return (
                    <div
                      key={f.file_id}
                      role="option"
                      aria-selected={on}
                      tabIndex={0}
                      title={f.file_name}
                      className={clsx(classes.pickerRow, on && classes.pickerRowOn)}
                      onClick={toggle}
                      onKeyDown={rowKeys(toggle)}
                    >
                      <Tick on={on} />
                      <span
                        className={classes.extBadge}
                        style={{ background: EXTENSION_COLORS[extensionFamily(f.extension)] }}
                        title={extensionTitle(f.extension)}
                      >
                        {extensionBadge(f.extension)}
                      </span>
                      <span className={classes.rowName}>{f.file_name}</span>
                      <span className={classes.infoTag} title={`document type: ${f.doc_type}`}>
                        {f.doc_type}
                      </span>
                      {f.file_category ? (
                        <span
                          className={clsx(classes.infoTag, classes.infoTagPlain)}
                          title={`file category: ${f.file_category}`}
                        >
                          {f.file_category}
                        </span>
                      ) : null}
                    </div>
                  );
                })
              )}
              {files && files.length >= FILE_LIMIT ? (
                <div className={classes.pickerMessage}>{TOO_MANY_FILES_TEXT}</div>
              ) : null}
            </>
          )}
        </div>
        {hasTypes ? <div className={classes.pickerFoot}>{pickerFooter(selection)}</div> : null}
      </div>
    </ClickAwayListener>
  );
}
